import { useReactiveVar } from '@apollo/client';
import { useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { activeQuestCompletions } from '../../client/apollo';
import { RitualType } from '../../client/generated/graphql';
import { ListQuest } from '../../client/types';
import { getRitualConfig } from '../../helpers/ritual';
import { RitualCardAnimation } from './RitualCardAnimation';
import { RitualCardCompletingState } from './RitualCardCompletingState';
import { RitualCardNormalState } from './RitualCardNormalState';

export interface RitualCardProps {
    quest: ListQuest;
    onClick: () => void;
    onActionClick?: () => void;
}

export const RitualCard: FC<RitualCardProps> = ({ quest, onClick, onActionClick }) => {
    const { t } = useTranslation();
    const activeCompletions = useReactiveVar(activeQuestCompletions);
    const matchingCompletion = activeCompletions.get(quest.id);
    const isCompleting = !!matchingCompletion;
    
    const ritualType = quest.ritualType as RitualType;
    const config = getRitualConfig(t)[ritualType];
    
    const subTasks = quest.subTasks ?? [];
    const completedCount = subTasks.filter(t => t.done).length;
    const totalCount = subTasks.length;
    const mainPressHandlers = useLiquidGlassPress({
        disabled: isCompleting,
        preventDefault: true,
        onActivate: () => {
            hapticFeedback.impactOccurred.ifAvailable('light');
            onClick();
        },
    });
    const actionPressHandlers = useLiquidGlassPress({
        disabled: isCompleting || !onActionClick,
        preventDefault: true,
        onActivate: onActionClick,
    });

    return (
        <RitualCardAnimation
            config={config}
            isCompleting={isCompleting}
            pressed={mainPressHandlers.pressed || actionPressHandlers.pressed}
        >
            {isCompleting ? (
                <RitualCardCompletingState
                    config={config}
                    completedXp={matchingCompletion.completedXp}
                    totalCount={totalCount}
                />
            ) : (
                <RitualCardNormalState
                    config={config}
                    completedCount={completedCount}
                    totalCount={totalCount}
                    mainPressHandlers={mainPressHandlers}
                    actionPressHandlers={onActionClick ? actionPressHandlers : undefined}
                />
            )}
        </RitualCardAnimation>
    );
};
